import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAsync, Table, Loading, ErrorBox, Empty, Field, Modal, Chip, fmtDate, useAction, MATCH_STATUS_OPTIONS } from "./AdminUI";

const INTEREST_TABS = [
  ["", "All"], ["pending_admin_review", "Pending Admin Review"], ["approved", "Delivered"],
  ["mutual", "Mutual Interest"], ["rejected", "Rejected"], ["declined", "Declined"],
];

export const AdminInterests = () => {
  const [status, setStatus] = useState("pending_admin_review");
  const { data, loading, error, reload } = useAsync(() => api.get(`/admin/interests?status=${status}`), [status]);
  const { busy, err, run } = useAction();

  const act = (id, action) =>
    run(
      () => api.post(`/admin/interests/${id}/${action}`, { note: action === "reject" ? "Not approved at this time" : "" }),
      () => { toast.success(`Interest ${action}d.`); reload(); },
    );

  return (
    <div className="ap-card">
      <h3 className="ap-card-title">Interest Management</h3>
      <p className="ap-note" style={{ marginBottom: 14 }}>Interests reach the receiver only after you approve them. Until then the receiver sees nothing.</p>
      <div className="ap-tabs" data-testid="ap-interest-tabs">
        {INTEREST_TABS.map(([k, label]) => (
          <button key={k || "all"} type="button" className={`ap-tab${k === status ? " is-active" : ""}`} onClick={() => setStatus(k)}>{label}</button>
        ))}
      </div>
      {err && <ErrorBox error={err} />}
      {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.interests.length === 0 ? <Empty>No interests in this view.</Empty> : (
        <Table columns={["Sender", "→", "Receiver", "Status", "Sent", "Decided", "Actions"]}>
          {data.interests.map((i) => (
            <tr key={i.id}>
              <td>{i.senderName || i.senderEmail}<br /><span className="ap-note">{i.fromProfileId}</span></td>
              <td>→</td>
              <td>{i.receiverName || i.receiverEmail}<br /><span className="ap-note">{i.toProfileId}</span></td>
              <td><Chip value={i.status} /></td>
              <td>{fmtDate(i.createdAt)}</td>
              <td>{i.decidedAt ? fmtDate(i.decidedAt) : "—"}</td>
              <td className="ap-table-actions">
                {i.status === "pending_admin_review" && (
                  <>
                    <button type="button" className="btn btn-solid btn-sm" data-testid={`ap-interest-approve-${i.id}`} disabled={busy} onClick={() => act(i.id, "approve")}>Approve</button>
                    <button type="button" className="btn btn-outline btn-sm" data-testid={`ap-interest-reject-${i.id}`} disabled={busy} onClick={() => act(i.id, "reject")}>Reject</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
};

export const AdminMatches = () => {
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/matches"), []);
  const [editId, setEditId] = useState(null);
  return (
    <>
      <div className="ap-card">
        <h3 className="ap-card-title">Matches &amp; Meetings</h3>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.matches.length === 0 ? <Empty>No match cases yet — a case is created when an interest becomes mutual.</Empty> : (
          <Table columns={["Case", "Profiles", "Status", "Contact", "Meeting", "Match Date", ""]}>
            {data.matches.map((m) => (
              <tr key={m.id}>
                <td>{m.id.slice(-8)}</td>
                <td>{m.profile_a} + {m.profile_b}</td>
                <td><Chip value={m.status} /></td>
                <td><Chip value={m.contact_status || "pending"} /></td>
                <td>{m.meeting?.date ? `${m.meeting.date} ${m.meeting.time || ""}` : "—"}</td>
                <td>{fmtDate(m.match_date)}</td>
                <td><button type="button" className="btn btn-outline btn-sm" data-testid={`ap-match-open-${m.id.slice(-6)}`} onClick={() => setEditId(m.id)}>Manage</button></td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      {editId && <MatchModal matchId={editId} onClose={() => { setEditId(null); reload(); }} />}
    </>
  );
};

const MatchModal = ({ matchId, onClose }) => {
  const { data, loading, error, reload } = useAsync(() => api.get(`/admin/matches/${matchId}`), [matchId]);
  const { busy, err, run } = useAction();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (data?.match) {
      const m = data.match;
      setForm({
        status: m.status, contact_status: m.contact_status || "pending",
        meeting: { date: m.meeting?.date || "", time: m.meeting?.time || "", location: m.meeting?.location || "", notes: m.meeting?.notes || "" },
        follow_up_notes: m.follow_up_notes || "",
      });
    }
  }, [data]);

  if (loading || !form) return <Modal title="Match Case" onClose={onClose} wide><Loading /></Modal>;
  const m = data.match;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMeeting = (k, v) => setForm((f) => ({ ...f, meeting: { ...f.meeting, [k]: v } }));

  return (
    <Modal title={`Match Case — ${m.profile_a} + ${m.profile_b}`} onClose={onClose} wide>
      {error && <ErrorBox error={error} />}
      {err && <ErrorBox error={err} />}
      <div className="ap-row">
        {["a", "b"].map((side) => (
          <div key={side} className="ap-card" style={{ marginTop: 0 }}>
            <h4 className="ap-card-title" style={{ fontSize: 17 }}>Side {side.toUpperCase()} — {m.sides?.[side]?.profileId}</h4>
            <dl className="ap-kv" style={{ gridTemplateColumns: "130px 1fr" }}>
              <dt>Name</dt><dd>{m.sides?.[side]?.fullName || "—"}</dd>
              <dt>Mobile</dt><dd>{m.sides?.[side]?.mobile || "—"}</dd>
              <dt>Email</dt><dd>{m.sides?.[side]?.email || "—"}</dd>
              <dt>Parent / Wali</dt><dd>{[m.sides?.[side]?.waliName, m.sides?.[side]?.waliRelationship, m.sides?.[side]?.waliMobile].filter(Boolean).join(" · ") || "—"}</dd>
              <dt>Preferred contact</dt><dd>{m.sides?.[side]?.preferredContact || "—"}</dd>
            </dl>
          </div>
        ))}
      </div>
      <p className="ap-note" style={{ margin: "10px 0 16px" }}>Full current addresses are never stored in match records and are never shown to the opposite side.</p>

      <div className="ap-row">
        <Field label="Case Status">
          <select className="ap-select" data-testid="ap-match-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
            {MATCH_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="Contact Status">
          <select className="ap-select" value={form.contact_status} onChange={(e) => set("contact_status", e.target.value)}>
            {["pending", "attempted", "contacted", "no_response"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <div className="ap-row">
        <Field label="Meeting Date"><input type="date" className="ap-input" value={form.meeting.date} onChange={(e) => setMeeting("date", e.target.value)} /></Field>
        <Field label="Meeting Time"><input type="time" className="ap-input" value={form.meeting.time} onChange={(e) => setMeeting("time", e.target.value)} /></Field>
        <Field label="Location (family-appropriate venue)"><input className="ap-input" value={form.meeting.location} onChange={(e) => setMeeting("location", e.target.value)} /></Field>
      </div>
      <div className="ap-row">
        <Field label="Meeting Notes"><textarea className="ap-textarea" style={{ minHeight: 70 }} value={form.meeting.notes} onChange={(e) => setMeeting("notes", e.target.value)} /></Field>
        <Field label="Follow-up Notes"><textarea className="ap-textarea" style={{ minHeight: 70 }} value={form.follow_up_notes} onChange={(e) => set("follow_up_notes", e.target.value)} /></Field>
      </div>
      <button
        type="button" className="btn btn-solid btn-sm" data-testid="ap-match-save" disabled={busy}
        onClick={() => run(() => api.put(`/admin/matches/${matchId}`, form), () => { toast.success("Match case updated."); onClose(); })}
      >
        {busy ? "Saving…" : "Save Case"}
      </button>
    </Modal>
  );
};

export const AdminDiscussions = () => {
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/discussions"), []);
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState(null);

  return (
    <>
      <div className="ap-card">
        <div className="ap-topbar" style={{ marginBottom: 8 }}>
          <h3 className="ap-card-title" style={{ margin: 0 }}>Matrimonial Discussion Board</h3>
          <button type="button" className="btn btn-solid btn-sm" data-testid="ap-discussion-new" onClick={() => setCreateOpen(true)}>New Discussion</button>
        </div>
        <p className="ap-note">This is a supervised matrimonial discussion facilitated by Suffa Matrimony. It is intended only for serious matrimonial communication.</p>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.discussions.length === 0 ? <Empty>No discussions yet.</Empty> : (
          <Table columns={["Title", "Participants", "Messages", "Enabled", "Status", "Created", ""]}>
            {data.discussions.map((d) => (
              <tr key={d.id}>
                <td>{d.title}{d.matchId ? <><br /><span className="ap-note">Case {d.matchId.slice(-8)}</span></> : null}</td>
                <td>{d.participants.map((p) => p.name).join(", ")}</td>
                <td>{d.messageCount}</td>
                <td><Chip value={d.enabled ? "approved" : "closed"}>{d.enabled ? "Enabled" : "Disabled"}</Chip></td>
                <td><Chip value={d.status} /></td>
                <td>{fmtDate(d.createdAt)}</td>
                <td><button type="button" className="btn btn-outline btn-sm" data-testid={`ap-discussion-open-${d.id.slice(-6)}`} onClick={() => setOpenId(d.id)}>Open</button></td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      {createOpen && <NewDiscussionModal onClose={() => { setCreateOpen(false); reload(); }} />}
      {openId && <DiscussionModal discussionId={openId} onClose={() => { setOpenId(null); reload(); }} />}
    </>
  );
};

const NewDiscussionModal = ({ onClose }) => {
  const { busy, err, run } = useAction();
  const [form, setForm] = useState({ title: "", emails: "", match_id: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Modal title="New Supervised Discussion" onClose={onClose}>
      {err && <ErrorBox error={err} />}
      <Field label="Title"><input className="ap-input" data-testid="ap-discussion-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. SM1013 + SM1014 family discussion" /></Field>
      <Field label="Participant emails (comma-separated)"><input className="ap-input" data-testid="ap-discussion-emails" value={form.emails} onChange={(e) => set("emails", e.target.value)} placeholder="family1@example.com, family2@example.com" /></Field>
      <Field label="Related Match/Case ID (optional)"><input className="ap-input" value={form.match_id} onChange={(e) => set("match_id", e.target.value)} /></Field>
      <button
        type="button" className="btn btn-solid btn-sm" data-testid="ap-discussion-create" disabled={busy}
        onClick={() => run(() => api.post("/admin/discussions", form), () => { toast.success("Discussion created."); onClose(); })}
      >
        {busy ? "Creating…" : "Create Discussion"}
      </button>
    </Modal>
  );
};

const DiscussionModal = ({ discussionId, onClose }) => {
  const { data, loading, error, reload } = useAsync(() => api.get(`/admin/discussions/${discussionId}`), [discussionId]);
  const { busy, err, run } = useAction();
  const [message, setMessage] = useState("");
  const [addEmails, setAddEmails] = useState("");

  if (loading) return <Modal title="Discussion" onClose={onClose} wide><Loading /></Modal>;
  const d = data?.discussion;

  return (
    <Modal title={d?.title || "Discussion"} onClose={onClose} wide>
      {error && <ErrorBox error={error} />}
      {err && <ErrorBox error={err} />}
      {d && (
        <>
          <p className="ap-note" style={{ marginBottom: 12 }}>
            This is a supervised matrimonial discussion facilitated by Suffa Matrimony. It is intended only for serious matrimonial communication.
          </p>
          <div className="ap-filters">
            <Chip value={d.enabled ? "approved" : "closed"}>{d.enabled ? "Enabled" : "Disabled"}</Chip>
            <Chip value={d.status} />
            <button
              type="button" className="btn btn-outline btn-sm" data-testid="ap-discussion-toggle"
              onClick={() => run(() => api.put(`/admin/discussions/${discussionId}`, { enabled: !d.enabled, status: d.enabled ? d.status : "open" }), reload)}
            >
              {d.enabled ? "Disable discussion" : "Enable discussion"}
            </button>
            {d.status === "open" ? (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => run(() => api.put(`/admin/discussions/${discussionId}`, { status: "closed" }), reload)}>Close discussion</button>
            ) : (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => run(() => api.put(`/admin/discussions/${discussionId}`, { status: "open" }), reload)}>Reopen</button>
            )}
          </div>

          <div className="ap-row" style={{ marginBottom: 14 }}>
            <Field label="Add participant emails"><input className="ap-input" value={addEmails} onChange={(e) => setAddEmails(e.target.value)} /></Field>
            <div style={{ alignSelf: "flex-end" }}>
              <button type="button" className="btn btn-outline btn-sm" disabled={busy || !addEmails} onClick={() => run(() => api.put(`/admin/discussions/${discussionId}`, { add_emails: addEmails }), () => { setAddEmails(""); reload(); })}>Add</button>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            {d.messages.length === 0 ? <Empty>No messages yet.</Empty> : d.messages.map((msg, i) => (
              <div key={i} className={`ap-msg${msg.sender_role === "admin" ? " ap-msg-admin" : ""}`}>
                <div className="ap-msg-meta">{msg.sender_name} · {fmtDate(msg.created_at)}</div>
                {msg.message}
              </div>
            ))}
          </div>

          <div className="ap-row">
            <Field label="Message as Suffa Matrimony Team"><input className="ap-input" data-testid="ap-discussion-msg" value={message} onChange={(e) => setMessage(e.target.value)} /></Field>
            <div style={{ alignSelf: "flex-end" }}>
              <button
                type="button" className="btn btn-solid btn-sm" disabled={busy || !message}
                onClick={() => run(() => api.post(`/admin/discussions/${discussionId}/messages`, { message }), () => { setMessage(""); reload(); })}
              >
                Send
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};
